/**
 * VoucherSection Component
 * Voucher display untuk checkout page
 * Navigate to cart page for voucher management (single source of truth)
 */

import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { ChevronRight } from '@mui/icons-material';
import { useRouter } from 'next/router';
import { memo } from 'react';

import { CheckIconVoucher } from '../../carts/Icons';

interface AppliedVoucher {
  voucher_code?: string;
  voucher_title?: string;
  reward_type?: string;
}

interface VouchersData {
  applied_vouchers?: AppliedVoucher[];
}

export interface VoucherSectionProps {
  /**
   * Vouchers data from orderData.vouchers
   * Contains applied_vouchers from saved order
   */
  vouchersData?: VouchersData;
}

/**
 * VoucherSection Component
 * Display applied vouchers from orderData.vouchers (NOT zustand store)
 * Follows separation of concerns: Cart page = manage vouchers, Checkout page = review only
 *
 * Performance optimized:
 * - Wrapped with React.memo
 */
function VoucherSectionInner({ vouchersData }: VoucherSectionProps) {
  const router = useRouter();

  // Get unique voucher codes from applied_vouchers
  // Note: There can be multiple entries for same voucher (one per division)
  const uniqueVoucherCodes = new Set(
    (vouchersData?.applied_vouchers || []).map((v) => v.voucher_code)
  );
  const voucherCount = uniqueVoucherCodes.size;
  const hasVouchers = voucherCount > 0;

  // Get first voucher for display (voucher code)
  const voucherCode = vouchersData?.applied_vouchers?.[0]?.voucher_code;

  /**
   * Navigate to cart page with query param to auto-open voucher modal
   * Desktop: Auto-opens modal
   * Mobile: Cart page redirects to /voucher page
   */
  const handleClick = () => {
    trackEvent(TRACKING_EVENTS.CHOOSE_VOUCHER, {
      page_name: TRACKING_PAGES.CHECKOUT,
      section_name: TRACKING_PAGES.CHECKOUT,
    });
    router.push('/cart?openVoucher=true');
  };

  return (
    <Box p={5}>
      {/* Gradient Border Wrapper */}
      <Box
        background="linear-gradient(25.45deg, #FF1E1E -6.15%, rgba(36, 81, 206, 0.9) 48.07%, rgba(90, 200, 255, 0.8) 94.92%)"
        borderRadius="md"
        p="2px"
      >
        {/* Inner Content */}
        <Flex
          as="button"
          type="button"
          bg="white"
          borderRadius="md"
          px={4}
          py={3}
          justify="space-between"
          align="center"
          w="full"
          cursor="pointer"
          border="none"
          onClick={handleClick}
          _hover={{ bg: 'gray.50' }}
          transition="background 0.2s"
        >
          <Flex align="center" gap={2}>
            {hasVouchers && <CheckIconVoucher boxSize="20px" flexShrink={0} />}
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {hasVouchers
                ? voucherCode || `${voucherCount} Voucher dipakai`
                : 'Gunakan voucher'}
            </Text>
          </Flex>

          <Flex align="center" gap={1}>
            <Text fontSize="sm" color="blue.600" fontWeight="semibold">
              {hasVouchers ? 'Ganti' : 'Pilih'}
            </Text>
            <ChevronRight style={{ color: '#2563eb', fontSize: '20px' }} />
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}

// Memoize to prevent unnecessary re-renders from parent
export const VoucherSection = memo(VoucherSectionInner);
